import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import Loader from '@/components/base/Loader'

import {
  MASK_TRANSITION_ENTER_DURATION,
  MASK_TRANSITION_LEAVE_DURATION,
  MASK_TRANSITION_STAGGER_AMOUNT
} from '@/contexts/transition'

export type MaskTransitionHandler = {
  enterTimeline: () => gsap.core.Timeline
  leaveTimeline: () => gsap.core.Timeline
}

const MaskTransition = forwardRef<MaskTransitionHandler>((_, ref) => {
  const root = useRef<HTMLDivElement>(null)

  const { contextSafe } = useGSAP({ scope: root })

  const enterTimeline = contextSafe(() => {
    return gsap
      .timeline()
      .set(root.current, { display: 'block' })
      .add(
        gsap.timeline().fromTo(
          ['[data-mask-green]', '[data-mask-cyan]', '[data-mask-white]'],
          { yPercent: 100 },
          {
            yPercent: 0,
            duration: MASK_TRANSITION_ENTER_DURATION,
            stagger: MASK_TRANSITION_STAGGER_AMOUNT,
            ease: 'power3.in',
            force3D: true
          }
        )
      )
      .add(
        gsap
          .timeline()
          .fromTo(
            '[data-mask-transparent]',
            { yPercent: 100 },
            {
              yPercent: 0,
              duration: MASK_TRANSITION_ENTER_DURATION,
              ease: 'power3.in',
              force3D: true
            }
          )
          .fromTo(
            ['[data-mask-logo]', '[data-mask-loader]'],
            { yPercent: -100 },
            {
              yPercent: 0,
              duration: MASK_TRANSITION_ENTER_DURATION,
              ease: 'power3.in',
              force3D: true
            },
            '<'
          ),
        `<+${MASK_TRANSITION_STAGGER_AMOUNT * 2}`
      )
    /* .fromTo(
        '[data-mask-logo-circle]',
        { strokeDashoffset: length },
        {
          strokeDashoffset: 0,
          duration: MASK_TRANSITION_ENTER_DURATION,
          ease: 'power3.inOut'
        },
        '<'
      ) */
  })

  const leaveTimeline = contextSafe(() => {
    return (
      gsap
        .timeline()
        /* .fromTo(
        '[data-mask-logo-circle]',
        { strokeDashoffset: 0 },
        {
          strokeDashoffset: -length,
          duration: MASK_TRANSITION_ENTER_DURATION,
          ease: 'power3.inOut'
        },
        '<'
      ) */
        .add(
          gsap
            .timeline()
            .fromTo(
              '[data-mask-transparent]',
              { yPercent: 0 },
              {
                yPercent: -100,
                duration: MASK_TRANSITION_LEAVE_DURATION,
                ease: 'power3.out',
                force3D: true
              }
            )
            .fromTo(
              ['[data-mask-logo]', '[data-mask-loader]'],
              { yPercent: 0 },
              {
                yPercent: 100,
                duration: MASK_TRANSITION_LEAVE_DURATION,
                ease: 'power3.out',
                force3D: true
              },
              '<'
            ),
          '<'
        )
        .add(
          gsap.timeline().fromTo(
            ['[data-mask-white]', '[data-mask-cyan]', '[data-mask-green]'],
            { yPercent: 0 },
            {
              yPercent: -100,
              duration: MASK_TRANSITION_LEAVE_DURATION,
              stagger: MASK_TRANSITION_STAGGER_AMOUNT,
              ease: 'power3.out',
              force3D: true
            }
          ),
          '<'
        )

        .set(root.current, { display: 'none' })
    )
  })

  useImperativeHandle(
    ref,
    () => ({
      leaveTimeline,
      enterTimeline
    }),
    [enterTimeline, leaveTimeline]
  )

  return (
    <div ref={root} className="fixed left-0 top-0 z-transition h-screen w-full">
      <div
        className="h-dvh-vh absolute left-0 top-0 w-full bg-[#99CC33]"
        data-mask-green
      />
      <div
        className="h-dvh-vh absolute left-0 top-0 w-full bg-[#009FE3]"
        data-mask-cyan
      />
      <div
        className="h-dvh-vh absolute left-0 top-0 w-full bg-white"
        data-mask-white
      />
      <div
        className="h-dvh-vh absolute left-0 top-0 w-full overflow-hidden"
        data-mask-transparent
      >
        <div className="h-dvh-vh absolute left-0 top-0 w-full" data-mask-logo>
          <svg
            viewBox="0 0 151 38"
            className="absolute left-1/2 top-1/2 h-[3.4rem] w-[12rem] -translate-x-1/2 -translate-y-1/2 fill-blue"
          >
            <path d="M21.1444 15.0174L10.1184 36.5356H3.85779L0.734182 15.0174L0 10.9727H5.69992C6.07368 14.2832 6.4341 17.7806 6.80787 21.0911C7.18163 24.4016 7.54205 27.8989 7.91581 31.2094C9.57106 27.8989 11.2263 24.4016 12.6947 21.0911C14.163 17.7806 16.0052 14.2832 17.6604 10.9727H22.9999L21.1444 15.0174Z" />
            <path d="M30.3418 6.36741C31.9703 6.36741 33.2785 5.05923 33.2785 3.43068C33.2785 1.80214 31.9703 0.493958 30.3418 0.493958C28.7132 0.493958 27.405 1.80214 27.405 3.43068C27.405 5.05923 28.7132 6.36741 30.3418 6.36741Z" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M27.0313 10.9727L26.1102 16.1253L22.4393 36.5356H27.2983C27.4585 36.5356 27.6187 36.5356 27.7655 36.5356L28.6866 31.3829L32.3708 10.9727H27.2182V11.0528L27.0313 10.9727Z"
            />
            <path d="M49.4706 10.9727C47.4282 10.9327 45.3992 11.1195 43.3969 11.52C41.9686 11.8137 40.607 12.3743 39.3923 13.1752C38.2576 13.9495 37.3632 15.0307 36.8159 16.2989C36.1485 17.9541 35.6546 19.6894 35.3476 21.4515L34.6134 25.683L32.7713 36.7225H37.9239L39.766 25.683L40.0463 23.2936L40.4201 21.2646C40.5002 20.0899 40.8873 18.9419 41.5281 17.9541C41.9953 17.1532 42.6227 16.459 43.3702 15.9251C44.1044 15.4846 44.932 15.231 45.7863 15.1776C47.1879 15.0174 48.6029 14.9506 50.0179 14.9907L49.4706 10.9727ZM97.8331 15.0174C97.3659 14.0696 96.6718 13.242 95.8041 12.6279C94.883 11.9338 93.8151 11.4266 92.6805 11.1729C91.4925 10.7992 90.251 10.6123 88.9962 10.6256C87.6614 10.6657 86.3131 10.7992 84.9916 10.9994C83.5766 11.213 82.2151 11.7202 80.987 12.4678C79.6254 13.282 78.4908 14.4167 77.6765 15.7783C76.6086 17.5136 75.9278 19.4625 75.6475 21.4782L74.9133 25.1624C74.5395 27.1114 74.5395 29.1003 74.9133 31.0492C75.3805 32.4242 76.128 33.6789 77.1158 34.7335C78.0903 35.6012 79.2783 36.2286 80.5465 36.5356C81.8547 36.896 83.2029 37.0829 84.5511 37.0829C86.2731 37.0562 87.9951 36.9227 89.7037 36.7091C91.2121 36.4688 92.6805 36.0951 94.1222 35.6012L93.4414 32.1171C92.3334 32.304 91.2388 32.4909 89.944 32.6644C88.3555 32.838 86.767 32.8914 85.1651 32.8513C84.4176 32.838 83.6567 32.7312 82.9359 32.5309C82.1617 32.264 81.4542 31.8101 80.9069 31.1961C80.2795 30.5153 79.8256 29.701 79.572 28.8066C79.1982 27.6053 79.1982 26.3238 79.572 25.1224L80.3062 21.625L80.4931 21.0777C80.7467 19.9431 81.1205 18.8351 81.601 17.7672C82.0682 16.9129 82.6956 16.1654 83.4432 15.5647C84.0572 15.0307 84.8314 14.7104 85.6457 14.6436C86.46 14.4834 87.2742 14.43 88.1019 14.4701C89.37 14.4033 90.6381 14.657 91.7861 15.2043C92.28 15.5113 92.6805 15.9518 92.9341 16.459C93.1878 16.9796 93.3079 17.5536 93.2545 18.141C93.2945 18.5415 93.2411 18.9419 93.1077 19.3157C92.9875 19.6894 92.7739 20.0499 92.5203 20.3435C91.9463 20.9309 91.2655 21.3847 90.4913 21.6784C89.5969 21.9854 88.6759 22.1723 87.7281 22.2257H84.7914C84.0572 22.2524 83.3097 22.1857 82.5888 22.0388L82.0415 25.5362H83.1495C84.431 25.7231 85.7258 25.7231 87.0073 25.5362C88.3021 25.4962 89.5836 25.376 90.8651 25.1624C92.0798 24.9889 93.2678 24.6151 94.3624 24.0545C95.3636 23.5205 96.2446 22.773 96.912 21.8519C97.6863 20.8374 98.1401 19.6227 98.2469 18.3546C98.4071 17.2199 98.2736 16.0719 97.8331 15.0174ZM149.319 15.0174C148.852 14.0696 148.158 13.242 147.29 12.6279C146.369 11.9338 145.301 11.4266 144.167 11.1729C142.979 10.7992 141.737 10.6123 140.482 10.6256C139.148 10.6657 137.799 10.7992 136.478 10.9994C135.076 11.1596 133.728 11.6802 132.58 12.5078C131.218 13.3221 130.084 14.4567 129.269 15.8183C128.202 17.5536 127.521 19.5026 127.24 21.5182L126.506 25.2025C126.132 27.1514 126.132 29.1404 126.506 31.0893C126.973 32.4642 127.721 33.719 128.709 34.7735C129.697 35.6279 130.871 36.2419 132.153 36.5356C133.461 36.896 134.809 37.0829 136.157 37.0829C137.879 37.0562 139.601 36.9227 141.31 36.7091C142.818 36.4688 144.287 36.0951 145.728 35.6012L144.994 32.2907C143.886 32.4776 142.792 32.6644 141.497 32.838C139.908 33.0115 138.307 33.0649 136.718 33.0249C135.971 32.9848 135.236 32.8647 134.516 32.6511C133.741 32.3841 133.034 31.9303 132.486 31.3162C131.859 30.6354 131.405 29.8212 131.152 28.9268C130.778 27.7254 130.778 26.4439 131.152 25.2425L132.033 21.8519L132.22 21.3046C132.473 20.17 132.847 19.0621 133.327 17.9942C133.795 17.1398 134.422 16.3923 135.17 15.7916C135.784 15.2577 136.558 14.9373 137.372 14.8705C138.16 14.7104 138.961 14.6436 139.762 14.6837C141.03 14.6169 142.298 14.8705 143.446 15.4178C144.367 15.6047 144.914 16.7527 144.914 18.181C144.954 18.5815 144.914 18.982 144.781 19.3557C144.647 19.7295 144.447 20.0899 144.193 20.3836C143.619 20.9709 142.939 21.4248 142.164 21.7185C141.27 22.0255 140.349 22.2124 139.401 22.2658H136.464C135.73 22.2924 134.983 22.2257 134.262 22.0789L133.715 25.5762H134.836C136.117 25.7631 137.412 25.7631 138.694 25.5762C139.988 25.5362 141.27 25.4161 142.551 25.2025C143.766 25.0289 144.954 24.6552 146.049 24.0945C147.063 23.5472 147.931 22.7864 148.598 21.8519C149.373 20.8374 149.827 19.6227 149.933 18.3546C150.134 17.2066 149.933 16.0185 149.333 15.0174H149.319ZM73.7386 15.0174L62.7392 36.5356H56.452L53.3284 15.0174L52.5942 11.0127H58.2941C58.6679 14.3232 59.0283 17.8206 59.402 21.1311C59.7758 24.4416 60.1362 27.939 60.51 31.2495C62.1652 27.939 63.8205 24.4416 65.2888 21.1311C66.7572 17.8206 68.5993 14.3232 70.2546 11.0127H75.5941L73.7386 15.0174Z" />
            <path
              opacity="0.85"
              d="M123.943 33.412C123.129 33.3853 122.328 33.2651 121.554 33.0382C120.793 32.8513 120.085 32.4776 119.525 31.9303C118.937 31.3563 118.484 30.6755 118.19 29.9012C118.03 29.434 117.963 28.9268 118.003 28.4329V27.3249C118.043 26.2704 118.177 25.2292 118.377 24.2013L118.804 22.5594L122.648 0.493927H117.469L114.933 14.657L113.598 22.7463L113.411 23.4805V23.8543C113.264 24.3882 113.198 24.9489 113.224 25.5095C112.904 27.5919 112.984 29.7144 113.464 31.7567C113.932 32.9581 114.693 34.026 115.667 34.8803C116.722 35.6546 117.91 36.2152 119.164 36.5356C120.473 36.8426 121.821 36.9627 123.169 36.9093H124.824L123.943 33.412Z"
              fill="#009FE3"
            />
            <path
              d="M111.249 33.412C110.434 33.3853 109.633 33.2651 108.859 33.0382C108.085 32.8647 107.364 32.4909 106.79 31.9436C106.203 31.3696 105.749 30.6888 105.455 29.9146C105.295 29.4474 105.228 28.9401 105.268 28.4462V27.3383C105.308 26.2837 105.442 25.2425 105.642 24.2147L106.016 22.5594L109.967 0.493927H104.815L102.238 14.657L100.903 22.3859L100.717 23.1201V23.4938C100.77 24.1746 100.703 24.8554 100.53 25.5228C100.196 27.5919 100.263 29.7144 100.717 31.7701C101.184 32.9715 101.945 34.0394 102.919 34.8937C103.987 35.6679 105.188 36.2152 106.47 36.5356C107.778 36.8426 109.126 36.9627 110.474 36.9093H112.13L111.249 33.412Z"
              fill="#99CC33"
            />
          </svg>
        </div>

        <div className="h-dvh-vh absolute left-0 top-0 w-full" data-mask-loader>
          <Loader className="absolute bottom-lg left-1/2 h-[5rem] w-[5rem] -translate-x-1/2 md:bottom-2xl" />
        </div>
      </div>
    </div>
  )
})

export default MaskTransition
